import React, { FC, useRef } from 'react';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import type { TransformedBanner } from 'app/api/services-strapi/banners/types';

import { useTranslation } from 'app/lang';
import { useViewportObserver } from 'app/lib/hooks/useViewportObserver';
import { ROUTE_PATH } from 'app/lib/routing/constants';
import { createGaEvent, dispatchGaEvent } from 'app/packages/analytics';

import { getCreativeSlotByPathname } from './utils/get-creative-slot-by-pathname';

import dictionary from './Banner.dictionary.json';

import styles from './Banner.module.scss';

type BannerProps = {
  title: TransformedBanner['title'];
  description: TransformedBanner['description'];
  image: TransformedBanner['image'];
  link: TransformedBanner['link'];
  promoId: TransformedBanner['promoId'];
  bannerId: TransformedBanner['id'];
  isPromo: TransformedBanner['isPromo'];
};

const Banner: FC<BannerProps> = ({
  description,
  title,
  image,
  link,
  promoId,
  bannerId,
  isPromo,
}) => {
  const { t } = useTranslation();
  const { location } = useHistory();
  const contentRef = useRef(null);
  const gaCreativeSlot = getCreativeSlotByPathname(location.pathname);
  const promoParam = promoId ? `?promotion_id=${promoId}` : location.pathname;
  const bannerContentClassName = classNames(styles.bannerContent, {
    [styles.bannerContentWithText]: title && description,
  });

  const sendViewGaEvent = () => {
    if (!isPromo){
      return;
    }

    const isPromotions = location.pathname.includes(ROUTE_PATH.PROMOTIONS);
    const creativeSlot = isPromotions ? 'page_promo' : 'main_carousel';

    dispatchGaEvent(createGaEvent(
      {
        event: 'view_promotion',
        promotion_id: bannerId,
        promotion_name: title || '',
        creative_slot: creativeSlot,
      },
    ));
  };
  useViewportObserver(contentRef, sendViewGaEvent);

  const dataGaAttributes = {
    ['data-ga-event']: 'select_promotion',
    ['data-ga-promotion_name']: title,
    ['data-ga-promotion-id']: bannerId,
    ['data-ga-creative-slot']: gaCreativeSlot,
  };

  return (
    <Link to={link || promoParam}
          ref={contentRef}
          className={styles.bannerContainer}
          {...dataGaAttributes}>
      {image && (
        <img src={image}
             className={styles.bannerImage}
             alt={t(dictionary.imageAltTitle, { title })} />
      )}

      <div className={bannerContentClassName}>
        {title && (
          <h3 className={styles.title}>
            {title}
          </h3>
        )}

        <p className={classNames(styles.description, { [styles.onlyDescription]: !title })}>
          {description}
        </p>
      </div>
    </Link>
  );
};

export default Banner;
